import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerMutation, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { removeEntity } from '@adornis/baseql/operations/mongo.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Permission } from '@adornis/buchungsheld-db/enums/roles.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';

@Entity()
export class LecturerComment extends MongoEntity {
  static override _class = 'LecturerComment';
  static override _collectionName = 'lecturer-comments';

  @Field(type => String, { default: v => v ?? A.getGloballyUniqueID() })
  override _id!: string;

  @Field(type => String)
  text: Maybe<string>;

  @Field(type => Date)
  createdAt: Maybe<Date>;

  @Field(type => String)
  lecturerID: Maybe<string>;

  @Field(type => String)
  createdBy: Maybe<string>;

  @Field(type => String)
  institutionID: Maybe<string>;
}

export const subscribeLecturerCommentsByLecturerIDAndInstitutionID = registerSubscription({
  operationName: 'subscribeLecturerCommentsByLecturerIDAndInstitutionID',
  type: () => [LecturerComment],
  params: [
    { name: 'lecturerID', type: () => String },
    { name: 'institutionID', type: () => String },
  ],
  resolve: (lecturerID: string, institutionID: string) => {
    return (gqlFields: BaseQLSelectionSet<LecturerComment>) => {
      return watchQuery(LecturerComment._collectionName, { institutionID, lecturerID });
    };
  },
});

export const createLecturerComment = registerMutation({
  operationName: 'createLecturerComment',
  resolve: (text: string, lecturerID: string, institutionID: string) => async () => {
    const currUser = await CurrentUserInfo.getMyself()({ _id: 1, roles: AdornisRoleToContextsWrapper.allFields });

    if (!context.serverContext && !currUser?.hasPermission(Permission.MANAGE_USER))
      throw new Error('No permission to add comments on lecturers');

    const newComment = {
      text: text,
      lecturerID: lecturerID,
      institutionID: institutionID,
      createdAt: new Date(),
      createdBy: currUser?._id,
    };

    const addCommentToCollection = await new LecturerComment(newComment).create();

    if (addCommentToCollection) return true;
    throw new Error(
      `Could not add new comment (text: ${text}; lecturerID: ${lecturerID}; institutionID: ${institutionID})`,
    );
  },
  type: () => Boolean,
  params: [
    { name: 'text', type: () => String },
    { name: 'lecturerID', type: () => String },
    { name: 'institutionID', type: () => String },
  ],
  options: { allowHTTP: true },
});

export const deleteLecturerCommentByID = registerMutation({
  operationName: 'deleteLecturerCommentByID',
  resolve: (lecturerCommentID: string) => async () => {
    console.log('deleteLecturerCommentByID');
    const currUser = await CurrentUserInfo.getMyself()({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!context.serverContext && !currUser?.hasPermission(Permission.MANAGE_USER))
      throw new Error('No permission to delete comments on lecturers');

    const removeResult = await removeEntity(LecturerComment, lecturerCommentID)();

    if (removeResult) return true;
    throw new Error(`Could not delete comment with id: ${lecturerCommentID}`);
  },
  type: () => Boolean,
  params: [{ name: 'lecturerCommentID', type: () => String }],
  options: { allowHTTP: true },
});
